<template>
  <div class="relative" ref="dropdownRef">
    <!-- Mobile: Simple row of buttons -->
    <div class="flex items-center gap-1 sm:hidden">
      <button
        v-for="loc in availableLocales"
        :key="loc.code"
        @click="switchLocale(loc.code)"
        :class="[
          'flex min-h-10 min-w-10 items-center justify-center rounded-lg px-2 text-xs font-medium transition-all duration-200',
          loc.code === locale
            ? 'bg-[var(--color-brand-secondary)] text-[var(--color-text-brand)] shadow-sm'
            : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-tertiary)]'
        ]"
        :aria-label="t('common.switchToLanguage', { language: loc.name })"
        :aria-pressed="loc.code === locale"
      >
        <span>{{ loc.code.toUpperCase() }}</span>
      </button>
    </div>

    <!-- Desktop: Dropdown -->
    <div class="hidden sm:block">
      <button
        @click="open = !open"
        class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-[var(--color-text-secondary)] hover:text-[var(--color-brand-secondary)] rounded-lg hover:bg-[var(--color-surface-secondary)] transition-colors"
        :aria-expanded="open"
        aria-haspopup="listbox"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>{{ currentLocaleName }}</span>
        <svg :class="['w-4 h-4 transition-transform', open ? 'rotate-180' : '']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
        </svg>
      </button>

      <div v-if="open" class="absolute top-full mt-1 z-50 bg-[var(--color-surface-primary)] rounded-lg shadow-lg border border-[var(--color-border-primary)] py-1 min-w-[160px] end-0">
        <button
          v-for="loc in availableLocales"
          :key="loc.code"
          @click="switchLocale(loc.code)"
          :class="[
            'w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors',
            loc.code === locale
              ? 'bg-[var(--color-brand-secondary)]/10 text-[var(--color-brand-secondary)] font-semibold'
              : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-secondary)]'
          ]"
          role="option"
          :aria-selected="loc.code === locale"
        >
          <span class="flex items-center gap-2">
            <span>{{ loc.name }}</span>
          </span>
          <svg v-if="loc.code === locale" class="w-4 h-4 text-[var(--color-brand-secondary)]" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';

const { locale, setLocale, locales: i18nLocales, t } = useI18n();

const open = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const availableLocales = computed(() =>
  i18nLocales.value as Array<{ code: string; name: string; dir: string }>
);

const currentLocaleName = computed(() => {
  const current = availableLocales.value.find(l => l.code === locale.value);
  return current ? current.name : locale.value;
});

async function switchLocale(code: string) {
  await setLocale(code as 'en' | 'ar' | 'zh');

  // Update dir attribute for RTL (client-side only)
  if (import.meta.client) {
    const loc = availableLocales.value.find(l => l.code === code);
    if (loc) {
      document.documentElement.dir = loc.dir || 'ltr';
      document.documentElement.lang = code;
    }
    // Persist to localStorage
    localStorage.setItem('a4t_admin_locale', code);
  }

  open.value = false;
}

// Close on outside click
function handleClickOutside(e: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    open.value = false;
  }
}

// Restore locale from localStorage on mount
onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  
  if (import.meta.client) {
    const savedLocale = localStorage.getItem('a4t_admin_locale');
    if (savedLocale && ['ar', 'en', 'zh'].includes(savedLocale) && savedLocale !== locale.value) {
      setLocale(savedLocale as 'ar' | 'en' | 'zh');
      const loc = availableLocales.value.find(l => l.code === savedLocale);
      if (loc) {
        document.documentElement.dir = loc.dir || 'ltr';
        document.documentElement.lang = savedLocale;
      }
    }
  }
});

onUnmounted(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>
